import "@doitintl/clouddiagrams-frontend/sass/client/_cmp_dashboard_list_item.scss";

import { useState } from "react";

import Typography from "@mui/material/Typography";
import Grid from "@mui/material/Grid2";
import Box from "@mui/material/Box";
import Card from "@mui/material/Card";
import CardContent from "@mui/material/CardContent";
import IconButton from "@mui/material/IconButton";
import Menu from "@mui/material/Menu";
import MenuItem from "@mui/material/MenuItem";
import MoreVertIcon from "@mui/icons-material/MoreVert";
import Popover from "@mui/material/Popover";
import TrendingUpIcon from "@mui/icons-material/TrendingUp";
import TrendingDownIcon from "@mui/icons-material/TrendingDown";

import awsLogoDark from "../../../assets/amazon-web-services-logo-white.png";
import awsLogo from "../../../assets/amazon-web-services-new-square.png";
import { useDarkThemeCheck } from "../../../Components/hooks/useDarkThemeCheck";
import { consoleErrorWithSentry } from "../../../utils";
import { useCustomerContext } from "../../../Context/CustomerContext";
import { useFullScreen } from "../../../utils/dialog";
import { useInfoSnackbar } from "../../../Components/SharedSnackbar/SharedSnackbar.context";
import { useApiContext } from "../../../api/context";
import { DeleteDialog } from "../../../Components/DeleteDialog";
import { deleteCloudDiagram } from "../libs";
import cdAppSettings from "../cdAppSetting";

import { timeRangeOpts, getCostsTimeRangeEnds } from "@doitintl/clouddiagrams-frontend/app/context/Costs";
import { dateParser, datePlusDays } from "@doitintl/clouddiagrams-frontend/app/libs/date_parser";
import { toFixed2 } from "@doitintl/clouddiagrams-frontend/app/libs/number_format";

export const getDiagramList = async (api, customer, setDiagramList) => {
  let date = new Date().getTime(),
    daysBackObj = {
      "24h": 1,
      "7d": 7,
      "28d": 28,
      "365d": 365,
    },
    start = new Date(date - daysBackObj["7d"] * 24 * 60 * 60 * 1000), // NOTE: days*hours*mins*secs*milisecs
    end = new Date(date);

  try {
    let { data } = await api.request({
      method: "get",
      baseURL: cdAppSettings.baseUrl,
      url: `/cloud-diagrams/api/scheme/stats`,
      params: {
        start,
        end,
        customer: customer.id,
      },
    });
    setDiagramList(data);
  } catch (err) {
    setDiagramList([]);
    consoleErrorWithSentry(err);
  }
};

export const getDiagramsTrending = async (
  api,
  customer,
  timeRange,
  setIsCostTrendingLoading,
  setCostDiagramsTrendingObject
) => {
  setIsCostTrendingLoading(true);

  const { start, end } = getCostsTimeRangeEnds(timeRange);

  try {
    const { data } = await api.request({
      method: "get",
      baseURL: cdAppSettings.baseUrl,
      url: "/cloud-diagrams/api/scheme/costs",
      params: {
        customer: customer?.id,
        start,
        end,
      },
    });
    setCostDiagramsTrendingObject(data);
  } catch (error) {
    consoleErrorWithSentry(error);
    setCostDiagramsTrendingObject({});
  } finally {
    setIsCostTrendingLoading(false);
  }
};

const BorderBox = ({ children, underline, onClick, ...props }) => {
  return (
    <Box
      className={`__item ${underline ? "_clickable" : ""}`}
      sx={{ borderColor: "divider" }}
      {...props}
      onClick={onClick || ((e) => e.stopPropagation())}
      data-cy={props["data-cy"] || "clouddiagrams-border-box"}
    >
      <Box className="__text" sx={{ borderColor: underline ? "text.secondary" : "transparent" }}>
        {children}
      </Box>
    </Box>
  );
};

const DiagramCard = ({ item, onDelete, isCostTrendingLoading, costDiagramsTrendingObject }) => {
  const [changesPopover, setChangesPopover] = useState(null);
  const [menuPopover, setMenuPopover] = useState(null);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);

  const darkMode = useDarkThemeCheck();
  const { customer } = useCustomerContext();
  const { isMobile } = useFullScreen();
  const showInfoSnackbar = useInfoSnackbar(3);
  const api = useApiContext();

  const openDiagram = () => {
    if (isMobile) {
      showInfoSnackbar("Cloud Diagrams are unavailable on mobile screens. Please use a desktop.");
      return;
    }
    window.location.href = `/cloud-diagrams/diagram/${item._id}/${item.ss_id}/${customer.id}`;
  };

  const changesLen = item.changes?.length || 0;

  const nodeCreateList = item.changes?.filter((c) => c.type === "NODE_CREATE");
  const nodeUpdateList = item.changes?.filter((c) => c.type === "NODE_UPDATE");
  const nodeDeleteList = item.changes?.filter((c) => c.type === "NODE_DELETE");

  const handleDelete = async () => {
    await deleteCloudDiagram({
      api,
      schemeId: item._id,
      customer,
      onSuccess: () => {
        onDelete(item._id);
      },
    });
  };

  const costTotal = costDiagramsTrendingObject[item._id]?.current?.total;
  const costTrending = costDiagramsTrendingObject[item._id]?.trending;

  return (
    <>
      <Card sx={{ cursor: "pointer" }} onClick={openDiagram} data-cy={`clouddiagrams-diagram-card-${item._id}`}>
        <CardContent sx={{ padding: "12px 16px 16px" }} data-cy="clouddiagrams-diagram-card-content">
          <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
            <Typography variant="h6" noWrap sx={{ mb: "-2px" }} data-cy="clouddiagrams-diagram-title">
              {item.name || "Unnamed"}
            </Typography>
            <IconButton
              onClick={(e) => {
                e.stopPropagation();
                setMenuPopover(e.currentTarget);
              }}
              data-cy="clouddiagrams-diagram-menu"
            >
              <MoreVertIcon />
            </IconButton>
          </Box>
          <Typography
            variant="body2"
            noWrap
            sx={{ color: "text.secondary", mb: "5px" }}
            data-cy="clouddiagrams-diagram-account"
          >
            {item.account_name ? `${item.account_name}${item.account_type ? ` (${item.account_type})` : ""}` : "-"}
          </Typography>
          <Box variant="body2" className="cd-diagram-card-bottomline" data-cy="clouddiagrams-diagram-details">
            <BorderBox data-cy="clouddiagrams-diagram-provider">
              <img
                src={darkMode ? awsLogoDark : awsLogo}
                width={24}
                alt="aws logo"
                style={{ marginBottom: "-3px" }}
                data-cy="clouddiagrams-diagram-provider-img"
              />
            </BorderBox>
            <BorderBox data-cy="clouddiagrams-diagram-regions-container">
              <Typography
                variant="body2"
                onClick={(e) => {
                  e.stopPropagation();
                }}
                data-cy="clouddiagrams-diagram-regions"
              >
                All regions
              </Typography>
            </BorderBox>
            <BorderBox
              underline={changesLen > 0}
              onClick={
                changesLen > 0
                  ? (e) => {
                      e.stopPropagation();
                      setChangesPopover((s) => (s ? null : e.target));
                    }
                  : null
              }
              onMouseEnter={changesLen > 0 ? (e) => setChangesPopover(e.target) : null}
              onMouseLeave={changesLen > 0 ? () => setChangesPopover(null) : null}
              data-cy="clouddiagrams-diagram-changes"
            >
              <Typography variant="body2" data-cy="clouddiagrams-diagram-changes-count">
                {changesLen} change{changesLen == 1 ? "" : "s"}
              </Typography>
            </BorderBox>
            {changesLen > 0 && (
              <Popover
                sx={{
                  pointerEvents: "none",
                }}
                open={!!changesPopover}
                anchorEl={changesPopover}
                anchorOrigin={{
                  vertical: "bottom",
                  horizontal: "center",
                }}
                transformOrigin={{
                  vertical: "top",
                  horizontal: "left",
                }}
                disableRestoreFocus
                slotProps={{
                  paper: {
                    style: {
                      width: "323px",
                      padding: "16px",
                    },
                  },
                }}
                data-cy="clouddiagrams-changes-popover"
              >
                <Typography
                  variant="subtitle1"
                  sx={{ fontWeight: "500", mb: "-14px" }}
                  data-cy="clouddiagrams-changes-title"
                >
                  Changes (last 7 days)
                </Typography>
                {nodeCreateList.length > 0 && (
                  <>
                    <Typography
                      variant="body2"
                      color="success"
                      sx={{ mt: "22px" }}
                      data-cy="clouddiagrams-changes-created-title"
                    >
                      Created
                    </Typography>
                    {nodeCreateList.map((c, i) => (
                      <Typography key={i} variant="body2" data-cy={`clouddiagrams-changes-created-item-${i}`}>
                        {c.count} {c.service} instance{c.count > 1 ? "s" : ""}
                      </Typography>
                    ))}
                  </>
                )}
                {nodeUpdateList.length > 0 && (
                  <>
                    <Typography
                      variant="body2"
                      color="text.secondary"
                      sx={{ mt: "22px" }}
                      data-cy="clouddiagrams-changes-updated-title"
                    >
                      Updated
                    </Typography>
                    {nodeUpdateList.map((c, i) => (
                      <Typography key={i} variant="body2" data-cy={`clouddiagrams-changes-updated-item-${i}`}>
                        {c.count} {c.service} instance{c.count > 1 ? "s" : ""}
                      </Typography>
                    ))}
                  </>
                )}
                {nodeDeleteList.length > 0 && (
                  <>
                    <Typography
                      variant="body2"
                      color="error"
                      sx={{ mt: "22px" }}
                      data-cy="clouddiagrams-changes-deleted-title"
                    >
                      Deleted
                    </Typography>
                    {nodeDeleteList.map((c, i) => (
                      <Typography key={i} variant="body2" data-cy={`clouddiagrams-changes-deleted-item-${i}`}>
                        {c.count} {c.service} instance{c.count > 1 ? "s" : ""}
                      </Typography>
                    ))}
                  </>
                )}
              </Popover>
            )}
            {!isCostTrendingLoading && (
              <BorderBox>
                <Typography
                  variant="body2"
                  onClick={(e) => {
                    e.stopPropagation();
                  }}
                  sx={{ display: "flex", alignItems: "center" }}
                >
                  {!!costTotal || costTotal === 0 ? `${"\u00A0"}${toFixed2(costTotal)}` : "-"}
                  <Box
                    component="span"
                    sx={{
                      color:
                        costTrending >= 25
                          ? "error.main"
                          : costTrending >= 10
                            ? "warning.main"
                            : costTrending < 0
                              ? "success.main"
                              : "inherit",

                      fontSize: "10px",
                      display: "flex",
                      alignItems: "center",
                    }}
                  >
                    {costTrending > 0 ? (
                      <TrendingUpIcon sx={{ height: "12px" }} />
                    ) : costTrending < 0 ? (
                      <TrendingDownIcon sx={{ height: "12px" }} />
                    ) : null}
                    {costTrending ? `${toFixed2(costTrending, "")}%` : "-"}
                  </Box>
                </Typography>
              </BorderBox>
            )}
          </Box>
        </CardContent>
        <Menu
          anchorEl={menuPopover}
          open={!!menuPopover}
          onClose={() => setMenuPopover(null)}
          onClick={(e) => e.stopPropagation()}
          data-cy="clouddiagrams-diagram-menu-popup"
          anchorOrigin={{
            vertical: "bottom",
            horizontal: "right",
          }}
          transformOrigin={{
            vertical: "top",
            horizontal: "right",
          }}
        >
          <MenuItem
            onClick={() => {
              setDeleteDialogOpen(true);
              setMenuPopover(null);
            }}
            data-cy="clouddiagrams-diagram-menu-delete"
          >
            <Typography color="error">Delete Diagram</Typography>
          </MenuItem>
        </Menu>
      </Card>
      <DeleteDialog
        open={deleteDialogOpen}
        title="Delete diagram"
        message="This diagram will be permanently deleted and won't be able to be recovered."
        deleteButtonText="Delete Diagram"
        onDelete={handleDelete}
        onClose={() => setDeleteDialogOpen(false)}
        dataCy="clouddiagrams-delete-dialog"
        confirmBtnDataCy="clouddiagrams-delete-dialog-confirm"
        mixpanelEventId="clouddiagrams.delete"
      />
    </>
  );
};

const DashboardList = ({
  searchQuery = "",
  diagramList,
  setDiagramList,
  isCostTrendingLoading,
  costDiagramsTrendingObject,
}) => {
  const handleDelete = (diagramId) => {
    setDiagramList((prevList) => prevList.filter((diagram) => diagram._id !== diagramId));
  };

  const filteredDiagrams = diagramList.filter(
    (diagram) =>
      diagram.name?.toLowerCase().includes(searchQuery) ||
      diagram.account_name?.toLowerCase().includes(searchQuery) ||
      diagram.account_type?.toLowerCase().includes(searchQuery)
  );

  return (
    <Grid container spacing={2} sx={{ padding: "0.5rem 0" }} data-cy="clouddiagrams-dashboard-list">
      {filteredDiagrams.map((c, i) => (
        <Grid size={{ xs: 12, sm: 12, md: 6, lg: 4, xl: 3 }} key={i} data-cy={`clouddiagrams-dashboard-list-item-${i}`}>
          <DiagramCard
            item={c}
            onDelete={handleDelete}
            costDiagramsTrendingObject={costDiagramsTrendingObject}
            isCostTrendingLoading={isCostTrendingLoading}
          />
        </Grid>
      ))}
    </Grid>
  );
};

export default DashboardList;
